import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { PublicLayout, Loading } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Compare from "./pages/Compare";
import Treatments from "./pages/Treatments";
import TreatmentDetail from "./pages/TreatmentDetail";
import Clinics from "./pages/Clinics";
import ClinicDetail from "./pages/ClinicDetail";
import ForClinics from "./pages/ForClinics";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Legal from "./pages/Legal";
import { ForgotPassword, ResetPassword, AcceptInvitation } from "./pages/AuthPages";
import CityPage, { CitiesIndex } from "./pages/CityPage";
import AkutPage, { AkutIndex } from "./pages/AkutPage";
import GuideIndex, { GuideDetail } from "./pages/Guides";
import VetCuraPet from "./pages/VetCuraPet";
const AdminPortal = lazy(() => import("./pages/admin/AdminPortal"));
const AdminDataQuality = lazy(() => import("./pages/admin/AdminDataQuality"));
const AdminOnboarding = lazy(() => import("./pages/admin/AdminOnboarding"));
const AdminLaunch = lazy(() => import("./pages/admin/AdminLaunch"));
const AdminDiscovery = lazy(() => import("./pages/admin/AdminDiscovery"));
const AdminCoverage = lazy(() => import("./pages/admin/AdminCoverage"));
const AdminNationalCollection = lazy(() => import("./pages/admin/AdminNationalCollection"));
const AdminLegalReview = lazy(() => import("./pages/admin/AdminLegalReview"));
const AdminBulkImport = lazy(() => import("./pages/admin/AdminBulkImport"));
const AdminActionCenter = lazy(() => import("./pages/admin/AdminActionCenter"));
const AdminClinicIndex = lazy(() => import("./pages/admin/AdminClinicIndex"));
const AdminPublication = lazy(() => import("./pages/admin/AdminPublication"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo"));
const AdminSync = lazy(() => import("./pages/admin/AdminSync"));
const AdminLogos = lazy(() => import("./pages/admin/AdminLogos"));
const OnboardingLanding = lazy(() => import("./pages/klinik/OnboardingLanding"));
const ClinicDashboard = lazy(() => import("./pages/klinik/ClinicDashboard"));
const KlinikOpret = lazy(() => import("./pages/klinik/KlinikOpret"));
const KlinikOvertag = lazy(() => import("./pages/klinik/KlinikOvertag"));
const KlinikVerificering = lazy(() => import("./pages/klinik/KlinikVerificering"));
const AdminVerifications = lazy(() => import("./pages/admin/AdminVerifications"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminClinics = lazy(() => import("./pages/admin/AdminClinics"));
const AdminClinicEdit = lazy(() => import("./pages/admin/AdminClinics").then((m) => ({ default: m.AdminClinicEdit })));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminPrices = lazy(() => import("./pages/admin/AdminPrices"));
const AdminSources = lazy(() => import("./pages/admin/AdminSources"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminClaims = lazy(() => import("./pages/admin/AdminClaims"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminAiAgent = lazy(() => import("./pages/admin/AdminAiAgent"));
const AdminPriceReview = lazy(() => import("./pages/admin/AdminPriceReview"));

const NotFound = () => (
  <div className="container-vp py-24 text-center" data-testid="not-found-page">
    <h1 className="text-4xl font-extrabold text-[#153C3A]">Siden findes ikke</h1>
    <Link to="/" className="btn-primary mt-6">Til forsiden</Link>
  </div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<Loading />}>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="sammenlign" element={<Compare />} />
              <Route path="behandlinger" element={<Treatments />} />
              <Route path="behandlinger/:slug" element={<TreatmentDetail />} />
              <Route path="dyreklinikker" element={<Clinics />} />
              <Route path="dyreklinikker/:slug" element={<ClinicDetail />} />
              <Route path="dyrlaege" element={<CitiesIndex />} />
              <Route path="dyrlaege/:slug" element={<CityPage />} />
              <Route path="akut-dyrlaege" element={<AkutIndex />} />
              <Route path="akut-dyrlaege/:slug" element={<AkutPage />} />
              <Route path="guide" element={<GuideIndex />} />
              <Route path="guide/:slug" element={<GuideDetail />} />
              <Route path="mit-dyr" element={<VetCuraPet />} />
              <Route path="for-klinikker" element={<ForClinics />} />
              <Route path="om-vetpris" element={<About />} />
              <Route path="kontakt" element={<Contact />} />
              <Route path="login" element={<Login />} />
              <Route path="glemt-adgangskode" element={<ForgotPassword />} />
              <Route path="nulstil-adgangskode" element={<ResetPassword />} />
              <Route path="invitation" element={<AcceptInvitation />} />
              <Route path="privatlivspolitik" element={<Legal page="privatliv" />} />
              <Route path="vilkaar" element={<Legal page="vilkaar" />} />
              <Route path="cookies" element={<Legal page="cookies" />} />
              <Route path="klinik/invitation" element={<OnboardingLanding />} />
              <Route path="klinik/dashboard" element={<ProtectedRoute><ClinicDashboard /></ProtectedRoute>} />
              <Route path="klinik/opret" element={<ProtectedRoute><KlinikOpret /></ProtectedRoute>} />
              <Route path="klinik/overtag" element={<ProtectedRoute><KlinikOvertag /></ProtectedRoute>} />
              <Route path="klinik/verificering" element={<ProtectedRoute><KlinikVerificering /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="admin" element={<ProtectedRoute admin><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminOverview />} />
              <Route path="klinikker" element={<AdminClinics />} />
              <Route path="klinikker/:id" element={<AdminClinicEdit />} />
              <Route path="behandlinger" element={<AdminServices />} />
              <Route path="priser" element={<AdminPrices />} />
              <Route path="kilder" element={<AdminSources />} />
              <Route path="brugere" element={<AdminUsers />} />
              <Route path="anmodninger" element={<AdminClaims />} />
              <Route path="verificering" element={<AdminVerifications />} />
              <Route path="klinikportal" element={<AdminPortal />} />
              <Route path="datakvalitet" element={<AdminDataQuality />} />
              <Route path="national-prisindsamling" element={<AdminNationalCollection />} />
              <Route path="juridisk-vurdering" element={<AdminLegalReview />} />
              <Route path="masseimport-priser" element={<AdminBulkImport />} />
              <Route path="prisindsamling-handlinger" element={<AdminActionCenter />} />
              <Route path="klinikindeks" element={<AdminClinicIndex />} />
              <Route path="publicering" element={<AdminPublication />} />
              <Route path="seo" element={<AdminSeo />} />
              <Route path="synkronisering" element={<AdminSync />} />
              <Route path="logoer" element={<AdminLogos />} />
              <Route path="klinik-invitationer" element={<AdminOnboarding />} />
              <Route path="lancering" element={<AdminLaunch />} />
              <Route path="klinikopdagelse" element={<AdminDiscovery />} />
              <Route path="daekning" element={<AdminCoverage />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="beskeder" element={<AdminMessages />} />
              <Route path="ai-prisagent" element={<AdminAiAgent />} />
              <Route path="prisgodkendelse" element={<AdminPriceReview />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
